import { useEffect, useState, useCallback } from 'react';
import { parseISO, isValid } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { apiRequest } from 'platform/utilities/api';
import { updateStateAndVerifier } from 'platform/utilities/oauth/utilities';
import { AUTHN_SETTINGS, SERVICE_PROVIDERS } from '../constants';
import {
  signupOrVerify,
  sessionTypeUrl,
  createExternalApplicationUrl,
} from '../utilities';

export function formatSignInTimestamp(
  isoDate,
  timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone,
) {
  if (!isoDate) return null;
  const parsedDate = parseISO(isoDate);
  if (!isValid(parsedDate)) return null;
  return formatInTimeZone(parsedDate, timeZone, "MMMM d, yyyy 'at' h:mm a zzz");
}

function transformSession(session) {
  return {
    handle: session?.handle,
    current: session?.status === 'current',
    isSignedOut: session?.status === 'signed_out',
    signedOutAt: session?.signedOutAt,
    deviceName: session?.deviceDescription,
    location: session?.location,
    signInDisplay: formatSignInTimestamp(session?.createdAt),
    credentialType: SERVICE_PROVIDERS[session?.cspType]?.label,
    browser: session?.browser,
  };
}

export function useSessions() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadSessions = useCallback(
    () =>
      apiRequest('/sign_in/sessions').then(response => {
        const data = response?.data;
        if (!Array.isArray(data)) {
          throw new Error('Unexpected /sign_in/sessions response shape');
        }
        setSessions(data.map(transformSession).filter(s => !s.isSignedOut));
      }),
    [],
  );

  useEffect(() => {
    loadSessions()
      .catch(err => setError(err))
      .finally(() => setIsLoading(false));
  }, [loadSessions]);

  const revokeSession = useCallback(
    handle => apiRequest(`/sign_in/sessions/${handle}`, { method: 'DELETE' }),
    [],
  );

  const revokeAllOtherSessions = useCallback(
    () => apiRequest('/sign_in/revoke_all_sessions', { method: 'POST' }),
    [],
  );

  const markSessionsSignedOut = useCallback(handles => {
    const signedOutAt = new Date().toISOString();
    setSessions(prev =>
      prev.map(session =>
        handles.includes(session.handle)
          ? { ...session, isSignedOut: true, signedOutAt }
          : session,
      ),
    );
  }, []);

  return {
    sessions,
    isLoading,
    error,
    loadSessions,
    revokeSession,
    revokeAllOtherSessions,
    markSessionsSignedOut,
  };
}

export function onVerifyClick({ useOAuth, policy }) {
  if (useOAuth) {
    updateStateAndVerifier(policy);
  }
}
/**
 *
 * @param {String} policy - Value can be either `logingov` or `idme`
 * @param {Boolean} useOAuth - Value should come from `isAuthenticatedWithOAuth` authentication selector
 * @returns {Object} Returns an object of the `href` and `onClick`
 */
export function useIdentityVerificationURL({ policy, useOAuth }) {
  const [href, setHref] = useState('');

  useEffect(() => {
    async function generateURL() {
      const url = await signupOrVerify({
        policy,
        allowVerification: true,
        isSignup: false,
        isLink: true,
        useOAuth,
      });
      setHref(url);
    }
    generateURL();
  }, [policy, useOAuth]);

  const onClick = useCallback(onVerifyClick, [useOAuth, policy]);
  return { href, onClick };
}

/**
 *
 * @param {Boolean} generateHref - Determines if href should be generated
 * @returns {String} OAuth URL for VA staff MHV login
 */
export function useInternalTestingAuth({ generateHref = true } = {}) {
  const [href, setHref] = useState('');

  useEffect(() => {
    if (!generateHref) return;

    async function generateURL() {
      const url = await sessionTypeUrl({
        type: 'mhv',
        useOauth: true,
        acr: 'loa3',
      });

      setHref(url);
    }

    generateURL();
  }, []);

  const onClick = () => {
    sessionStorage.setItem(
      AUTHN_SETTINGS.RETURN_URL,
      createExternalApplicationUrl(),
    );
  };

  return { href, onClick };
}
